import { filterNotEmpty } from '@beeline.com/bp-design-system';
import { useTenantSettingsPageIntl } from '@oc/pages/settings/tenant/translations/tenantSettingsPageIntl';
import { useStaticPermissions } from 'common/auth/Authorize';
import type { NavItem } from 'common/HorizontalNavPage';
import { LaunchDarklyFeatureFlagKeys } from 'integrations/launchDarkly/legacy/LaunchDarklyFeatureFlags.types';
import { useDarkly } from 'integrations/launchDarkly/legacy/useDarkly';
import { TABS } from './TenantSettingsPage.types';

export function useTenantNavItems() {
  const {
    workerProfileGlobalId: isGlobalIdEnabled,
    teamsNotification: isTeamsNotificationEnabled,
    workdayConfiguration: isWorkdayConfigurationEnabled
  } = useDarkly();

  const staticPermission = useStaticPermissions();
  const intl = useTenantSettingsPageIntl();
  const globalIdPermission = staticPermission({ permissionName: 'GLOBAL_ID_FORMULA' }).UPDATE;
  const canUpdateTenants = staticPermission({ permissionName: 'TENANT_CONFIG' }).UPDATE;
  const canUserReadSecrets = staticPermission({ permissionName: 'TENANT_SECRETS' }).READ;
  const showAppearance = canUpdateTenants;

  return {
    flags: {
      isGlobalIdEnabled,
      isTeamsNotificationEnabled,
      isWorkdayConfigurationEnabled,
      globalIdPermission,
      canUpdateTenants,
      canUserReadSecrets,
      showAppearance
    },
    navItems: filterNotEmpty<NavItem>([
      {
        label: intl.exports,
        path: TABS.EXPORT,
        'data-test': 'export__config__tab',
        permissions: [{ name: 'TENANT_CONFIG', action: 'READ' }]
      },
      {
        label: intl.imports,
        path: TABS.IMPORT,
        'data-test': 'import__config__tab',
        permissions: [{ name: 'TENANT_CONFIG', action: 'READ' }]
      },

      {
        label: intl.ymlConfiguration,
        path: TABS.YML_SETTINGS,
        'data-test': 'yml__config__tab',
        permissions: [{ name: 'TENANT_CONFIG', action: 'READ' }]
      },
      {
        label: intl.fieldMetadata,
        path: TABS.FIELD_METADATA_EDITOR,
        'data-test': 'fieldmetadata__config__tab',
        permissions: [{ name: 'TENANT_CONFIG', action: 'READ' }]
      },
      isGlobalIdEnabled &&
        globalIdPermission && {
          label: intl.globalId,
          permissions: [{ name: 'GLOBAL_ID_FORMULA', action: 'UPDATE' }],
          path: TABS.GLOBAL_ID,
          'data-test': 'global-id_config_tab',
          featureFlagDarkly: LaunchDarklyFeatureFlagKeys.workerProfileGlobalId
        },
      {
        label: intl.dateAndTime,
        path: TABS.DATE_TIME_FORMAT,
        'data-test': 'date_time_format',
        permissions: [{ name: 'TENANT_CONFIG', action: 'READ' }]
      },
      showAppearance && {
        label: intl.appearance,
        permissions: [{ name: 'TENANT_CONFIG', action: 'UPDATE' }],
        path: TABS.THEME,
        'data-test': 'theme__config__tab'
      },
      {
        label: intl.adminHelpers,
        path: TABS.ADMIN_HELPERS,
        'data-test': 'admin-helpers__tab',
        permissions: [{ name: 'TENANT_CONFIG', action: 'READ' }]
      },
      {
        label: intl.email,
        path: TABS.EMAIL_SETTINGS,
        'data-test': 'email-settings__tab',
        permissions: [{ name: 'TENANT_CONFIG', action: 'READ' }]
      },
      isTeamsNotificationEnabled && {
        label: intl.notifications,
        path: TABS.NOTIFICATIONS_SETTINGS,
        'data-test': 'notifications-settings__tab',
        permissions: [{ name: 'TENANT_CONFIG', action: 'READ' }],
        featureFlagDarkly: LaunchDarklyFeatureFlagKeys.teamsNotification
      },
      isWorkdayConfigurationEnabled &&
        canUserReadSecrets && {
          label: intl.workday,
          path: TABS.WORKDAY_CONFIG,
          'data-test': 'workday__tab',
          permissions: [{ name: 'TENANT_SECRETS', action: 'READ' }],
          featureFlagDarkly: LaunchDarklyFeatureFlagKeys.workdayConfiguration
        }
    ])
  };
}
